import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  CheckCircle2, 
  ArrowLeft,
  Calendar, 
  Users, 
  BookOpen, 
  GraduationCap, 
  Clock, 
  Building,
  ExternalLink,
  ArrowRight,
  MapPin,
  User,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useClassWizard } from './ClassWizardContext';
import { formatDate } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { StatusBadge } from '@/components/common/StatusBadge';

// Interfaces para tipagem dos dados
interface CourseData {
  id: string;
  name: string;
  description?: string;
  duration_minutes?: number;
}

interface CustomerData {
  id: string;
  name: string;
  legal_name?: string;
}

interface CustomerRelationshipData {
  id: string;
  customer_id: string;
  child_id: string;
  relationship?: string;
  customer: CustomerData;
}

interface SubscriptionData {
  id: string;
  display_id: string;
  customer_relationship: CustomerRelationshipData;
}

interface ClassData {
  id: string;
  display_id: string;
  name: string;
  description?: string;
  start_date: string;
  end_date: string;
  max_participants?: number;
  status: string;
  created_at: string;
  courses: CourseData;
  subscriptions?: SubscriptionData;
  instructor_name?: string;
  location?: string;
}

const ClassConfirmation = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const classId = id || (location.state as any)?.classId;
  
  // Tente usar o contexto, mas não falhe se não estiver disponível
  let resetWizard = () => {};
  try {
    const wizardContext = useClassWizard();
    resetWizard = wizardContext?.resetWizard || (() => {});
  } catch (error) {
    console.log('ClassWizard context not available, continuing without it');
  }
  
  const [classData, setClassData] = useState<ClassData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (classId) {
      fetchClassDetails(classId);
    } else {
      setError('ID da classe não fornecido');
      setLoading(false);
    }
    
    // Tente resetar o wizard quando a página for carregada, se disponível
    try {
      resetWizard();
    } catch (error) {
      console.log('Could not reset wizard state');
    }
  }, [classId]);

  const fetchClassDetails = async (id: string) => {
    setLoading(true);
    setError('');
    try {
      const { data, error } = await supabase
        .from('classes')
        .select(`
          *,
          courses:course_id (
            id,
            name,
            description,
            duration_minutes
          ),
          subscriptions:subscription_id (
            id,
            display_id,
            customer_relationship:customer_relationship_id (
              id,
              customer_id,
              child_id,
              relationship,
              customer:customers!fk_child (
                id,
                name,
                legal_name
              )
            )
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Classe não encontrada');
      
      setClassData(data);
    } catch (error: any) {
      console.error('Erro ao carregar detalhes da classe:', error);
      setError(error.message || 'Erro ao carregar detalhes da classe');
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'Agendada';
      case 'in_progress':
        return 'Em Andamento';
      case 'completed':
        return 'Concluída';
      case 'cancelled':
        return 'Cancelada';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
          <p className="text-lg text-muted-foreground">Carregando dados da formação...</p>
        </div>
      </div>
    );
  }

  if (error || !classData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <Card className="w-full max-w-md bg-white/60 backdrop-blur-sm border shadow-sm">
          <CardHeader>
            <CardTitle className="text-center text-red-500">Erro</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center">{error || 'Não foi possível carregar os dados da formação'}</p>
          </CardContent>
          <CardContent className="flex justify-center">
            <Button onClick={() => navigate('/classes')}>Voltar para Formações</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Success Hero Section */}
      <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
        {/* SVG Background Element */}
        <div className="absolute inset-0 pointer-events-none">
          <svg 
            className="opacity-30"
            width="1690"
            height="1026.5433"
            viewBox="0 0 826.54 502.06" 
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid slice"
          >
            <g transform="scale(1.1) translate(-70, -315)">
              <path d="M0 0h826.54v502.06H0z" fill="none"/>
              <path d="M0 0h826.54v502.06H0z" fill="url(#a)"/>
              <defs>
                <linearGradient id="a" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity=".1"/>
                  <stop offset="100%" stopColor="#8B5CF6" stopOpacity=".1"/>
                </linearGradient>
              </defs>
            </g>
          </svg>
        </div>
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="mb-8 flex justify-center">
            <div className="bg-white/20 backdrop-blur-sm p-6 rounded-full ring-4 ring-white/30">
              <CheckCircle2 className="h-20 w-20 text-white" />
            </div>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
            Formação agendada com sucesso!
          </h1>
          
          <p className="text-xl text-white/90 mb-6">
            A formação foi agendada e está pronta para ser executada.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="outline" 
              size="lg"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm transition-all duration-300"
              onClick={() => navigate('/classes')}
            >
              Ver Todas as Formações
            </Button>
            <Button 
              size="lg"
              className="bg-white text-green-600 hover:bg-white/90 transition-all duration-300"
              onClick={() => navigate(`/class-details/${classData.id}`)}
            >
              Ver Detalhes
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Class Information */}
            <Card className="bg-white/60 backdrop-blur-sm border shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <BookOpen className="h-5 w-5 mr-2 text-blue-600" />
                  Informações da Formação
                </CardTitle>
                <CardDescription>
                  ID: {classData.display_id || classData.id.substring(0, 8).toUpperCase()}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                    <BookOpen className="h-5 w-5 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Nome da Formação</p>
                      <p className="font-semibold text-gray-900">{classData.name}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                    <GraduationCap className="h-5 w-5 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Curso</p>
                      <p className="font-semibold text-gray-900">{classData.courses?.name || '-'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                    <Calendar className="h-5 w-5 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Data de Início</p>
                      <p className="font-semibold text-gray-900">{formatDateTime(classData.start_date)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                    <Clock className="h-5 w-5 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Data de Término</p>
                      <p className="font-semibold text-gray-900">{formatDateTime(classData.end_date)}</p>
                    </div>
                  </div>
                  
                  {classData.max_participants && (
                    <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                      <Users className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-600">Máximo de Participantes</p>
                        <p className="font-semibold text-gray-900">{classData.max_participants}</p>
                      </div>
                    </div>
                  )}
                  
                  {classData.location && (
                    <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                      <MapPin className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-600">Local</p>
                        <p className="font-semibold text-gray-900">{classData.location}</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {classData.description && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">{classData.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Course Details */}
            <Card className="bg-white/60 backdrop-blur-sm border shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <GraduationCap className="h-5 w-5 mr-2 text-purple-600" />
                  Detalhes do Curso
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                    <BookOpen className="h-5 w-5 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Nome do Curso</p>
                      <p className="font-semibold text-gray-900">{classData.courses?.name || '-'}</p>
                    </div>
                  </div>
                  
                  {classData.courses?.duration_minutes && (
                    <div className="flex items-center space-x3 p-4 bg-gray-50 rounded-lg">
                      <Clock className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-600">Duração</p>
                        <p className="font-semibold text-gray-900">{classData.courses.duration_minutes} minutos</p>
                      </div>
                    </div>
                  )}
                  
                  {classData.courses?.description && (
                    <div className="md:col-span-2 p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-600 mb-2">Descrição</p>
                      <p className="text-gray-900">{classData.courses.description}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Status and Actions */}
            <Card className="bg-white/60 backdrop-blur-sm border shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <Clock className="h-5 w-5 mr-2 text-green-600" />
                  Status e Ações
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Clock className="h-5 w-5 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Status</p>
                      <Badge className={`${getStatusColor(classData.status)}`}>
                        {getStatusLabel(classData.status)}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-600">Criada em</p>
                    <p className="font-semibold text-gray-900">{formatDateTime(classData.created_at)}</p>
                  </div>
                </div>
                
                <Separator className="my-4" />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button 
                    onClick={() => navigate(`/class-details/${classData.id}`)}
                    className="w-full gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Ver Detalhes Completos
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/class-wizard')}
                    className="w-full gap-2"
                  >
                    <GraduationCap className="h-4 w-4" />
                    Agendar Nova Formação
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Customer & Summary */}
          <div className="space-y-6">
            {/* Customer Information */}
            {classData.subscriptions?.customer_relationship && (
              <Card className="bg-white/60 backdrop-blur-sm border shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center text-xl">
                    <Building className="h-5 w-5 mr-2 text-indigo-600" />
                    Cliente
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200">
                        Cliente
                      </Badge>
                    </div>
                    
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {classData.subscriptions.customer_relationship.customer.name}
                    </h4>
                    
                    {classData.subscriptions.customer_relationship.customer.legal_name && (
                      <div className="text-sm text-gray-600 mb-2">
                        {classData.subscriptions.customer_relationship.customer.legal_name}
                      </div>
                    )}
                    
                    {classData.subscriptions.customer_relationship.relationship && (
                      <div className="text-sm text-gray-600">
                        Tenant: {classData.subscriptions.customer_relationship.relationship}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <Card className="bg-white/60 backdrop-blur-sm border shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Ações Rápidas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/classes')}
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  Ver Todas as Formações
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/class-wizard')}
                >
                  <GraduationCap className="h-4 w-4 mr-2" />
                  Agendar Nova Formação
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate(`/class-details/${classData.id}`)}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Ver Detalhes Completos
                </Button>
              </CardContent>
            </Card>

            {/* Summary Card */}
            <Card className="bg-white/60 backdrop-blur-sm border shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Resumo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-600">Formação</span>
                  <span className="font-semibold text-gray-900">{classData.name}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-600">Curso</span>
                  <span className="font-semibold text-gray-900">{classData.courses?.name || '-'}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-600">Status</span>
                  <Badge className={`${getStatusColor(classData.status)}`}>
                    {getStatusLabel(classData.status)}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassConfirmation; 